import { lazy, Suspense, useEffect, useState } from 'react';
import * as React from 'react';

import Image from '@/components/elements/Image';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import useHashScroll from '@/hooks/scroll/useHashScroll/HashScroll';
import { useRegisterEntryCampaignMutation } from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { Campaign } from '@/lib/microcms/model';
import { htmlToThemed } from '@/lib/microcms/parseHTML';
import { parseCmsSrcset } from '@/lib/microcms/utils';
import { ENTERING_KEY } from '@/pages/News/Id';
import { defaultTheme } from '@/utility/theme';

import ShareLinks from '../Partials/ShareLinks';
import { BodyWrapper, ContentHeader, PublishedAt, Title } from '../style';
import FloatEntryButton from './FloatEntryButton';

const LoginModal = lazy(() => import('@/components/layouts/Modal/Login'));
const ErrorModal = lazy(() => import('@/components/layouts/Modal/Error'));

interface CampaignContentBodyProps {
  content: Campaign;
  isEnteredEntryCampaign: boolean;
}

const CampaignContentBody: React.FC<CampaignContentBodyProps> = ({
  content,
  isEnteredEntryCampaign,
}) => {
  const { isMember } = useUserContext();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [entryButton, setEntryButton] = useState<HTMLButtonElement | null>();
  const [isOpenLoginModal, setIsOpenLoginModal] = useModal(false);
  const [isEntered, setIsEntered] = useState<boolean>(isEnteredEntryCampaign);

  const [registerEntryCampaignMutation, registerEntryCampaignMutationResult] =
    useRegisterEntryCampaignMutation();

  const toDisabled = () => {
    entryButton!.style!.cursor = 'inherit';
    entryButton!.style!.opacity =
      defaultTheme.button.opacity.disabled.toString();
    entryButton!.setAttribute('disabled', 'true');
    entryButton!.innerHTML = 'エントリー済み';
  };

  const entryCampaignFnc = () => {
    if (isProcessing) return;
    if (!isMember) {
      setIsOpenLoginModal(true);
      return;
    }
    setIsProcessing(true);
    registerEntryCampaignMutation({
      variables: {
        entryCampaignId: content.id,
      },
    })
      .then(_ => {
        toDisabled();
        setIsEntered(true);
      })
      .catch(() => {
        setIsOpenErrorModal(true);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    setEntryButton(
      document.getElementById('entry-button') as HTMLButtonElement | null,
    );
  }, []);

  useEffect(() => {
    setIsEntered(isEnteredEntryCampaign);

    if (entryButton && content) {
      entryButton.addEventListener('click', entryCampaignFnc);
      if (isEnteredEntryCampaign) {
        toDisabled();
      } else if (!dayjst().isBetween(content.startTime, content.endTime)) {
        toDisabled();
        entryButton.innerHTML = '現在エントリー期間外です';
      }
    }
    return () => {
      if (entryButton) {
        entryButton.removeEventListener('click', entryCampaignFnc);
      }
    };
  }, [isEnteredEntryCampaign, entryButton]);

  useHashScroll();

  return (
    <>
      <Image
        srcSet={parseCmsSrcset({ imageUrl: content.featuredImage.url })}
        mediaSet={[
          {
            srcSet: parseCmsSrcset({
              imageUrl: content.featuredImage.url,
              format: 'webp',
            }),
            type: 'image/webp',
          },
        ]}
        aspectRatio={content.featuredImage.height / content.featuredImage.width}
      />
      <ContentHeader>
        <PublishedAt>
          {dayjst(content.publishedAt || content.updatedAt).format('YYYY/M/D')}
        </PublishedAt>
        <Title>{content.title}</Title>
      </ContentHeader>
      <BodyWrapper>
        {htmlToThemed(content.body_v2 || content.body || '')}
      </BodyWrapper>
      <ShareLinks nonPublic={content.nonPublic} />
      <Suspense fallback={<></>}>
        <LoginModal
          isOpen={isOpenLoginModal}
          handleClose={() => setIsOpenLoginModal(false)}
          backPath={`/campaign/${content.id}`}
          urlParams={new URLSearchParams(`${ENTERING_KEY}=true`)}
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <ErrorModal
          isOpen={isOpenErrorModal}
          handleClose={() => setIsOpenErrorModal(false)}
          apolloError={registerEntryCampaignMutationResult.error}
        />
      </Suspense>
      {content && (
        <FloatEntryButton
          isEntered={isEntered}
          campaign={content}
          onClick={() => entryCampaignFnc()}
        />
      )}
    </>
  );
};

export default CampaignContentBody;
